
import React, { useState, useEffect, useRef } from 'react';
import { X, Package, Palette, Check, ChevronRight, ChevronLeft, Store, Archive, AlertCircle, Info, Layers, Tag, Sparkles, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Product, UserRole, ProductSize, Collections } from '../../types';
import { useToast } from '../../contexts/ToastContext';

interface ProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (payload: Product) => void;
    product?: Product | null;
    // isEditing: boolean;
    onCollectionAdded: (collection: Collections) => void; // 👈 ADD THIS

    prefilledId?: string | null;
    readOnly?: boolean;
    collections: Collections[];
    userRole: UserRole | null;
    initialStep?: number;
}

const SIZE_OPTIONS = [
    { size: "None" },
    { size: "1.0" },
    { size: "1.5" },
    { size: "2.0" },
    { size: "2.5" },
    { size: "3.0" }
];

const ProductModal: React.FC<ProductModalProps> = ({
    isOpen,
    onClose,
    onSave,
    product,
    // isEditing,
    onCollectionAdded,
    prefilledId,
    readOnly = false,
    collections,
    userRole,
    initialStep = 0,
}) => {
    const { addToast } = useToast();
    const [step, setStep] = useState(initialStep);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{ name?: boolean; collection?: boolean; price?: boolean }>({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);

    const [newCollection, setNewCollection] = useState({
        collectionName: "",
    });
    const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
    const [variantStocks, setVariantStocks] = useState<Record<string, { store: string; wh: string }>>({});
    const [originalStocks, setOriginalStocks] = useState<ProductSize[]>([]);

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        collection: '', // should be from the collection render list from App.tsx 
        price: 0,
        description: '',
        image: '',
        material: '',
    });



    useEffect(() => {
        if (!isCollectionModalOpen) return;

        // Reset dropdown so it doesn't stay on "__add_new__"
        setFormData((prev) => ({
            ...prev,
            collection: "",
        }));
    }, [isCollectionModalOpen]);

    const isEditMode = !!product;
    // Warehouse managers are no longer locked out of store stock in the inventory engine module
    const isStoreLocked = userRole === 'Viewer';

    useEffect(() => {
        if (isOpen) {
            setStep(initialStep);
            setError(null);
            if (product) {
                const initialStocks: Record<string, { store: string; wh: string }> = {};
                product.stocks.forEach(s => {
                    initialStocks[s.size] = {
                        store: s.stock.toString(),
                        wh: s.warehouseStock.toString()
                    };
                });

                setVariantStocks(initialStocks);
                setSelectedSizes(product.stocks.map(s => s.size));
                setOriginalStocks(product.stocks);

                setFormData({
                    id: product.id,
                    name: product.name,
                    collection: product.collection || '',
                    price: product.price.toString(),
                    description: product.description || '',
                    image: product.image || '',
                    material: product.material || '',
                });
            } else {
                setSelectedSizes([]);
                setVariantStocks({});
                setFormData({
                    id: prefilledId || '',
                    name: '',
                    collection: '',
                    price: '',
                    description: '',
                    image: '',
                    material: '',
                });
            }
        }
    }, [isOpen, product, prefilledId]);

    if (!isOpen) return null;

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                setError("Image size exceeds 2MB limit.");
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, image: reader.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddCollection = async () => {
        if (!newCollection.collectionName.trim()) {
            setError("Collection Name is required.");
            return;
        }

        try {
            const response = await fetch(process.env.COLLECTION_BASE_URL!, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                // Backend expects: { collectionName }
                body: JSON.stringify({ collectionName: newCollection.collectionName.trim() }),
            });

            const data = await response.json();

            if (data.OK) {
                // Backend returns { collectionId, collectionName, ... }
                const raw = data.DATA;
                const created: Collections = {
                    _id: raw.collectionId,
                    id: raw.collectionId,
                    name: raw.collectionName,
                };

                onCollectionAdded(created);

                setFormData(prev => ({
                    ...prev,
                    collection: created.name,
                }));

                setNewCollection({ collectionName: "" });
                setIsCollectionModalOpen(false);
            } else {
                setError(data.MSG || "Failed to add collection.");
            }
        } catch (err) {
            console.error(err);
            setError("Network error while adding collection.");
        }
    };

    const clearImage = () => {
        setFormData(prev => ({ ...prev, image: '' }));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleToggleSize = (size: string) => {
        if (selectedSizes.includes(size)) {
            setSelectedSizes(prev => prev.filter(s => s !== size));
            const newStocks = { ...variantStocks };
            delete newStocks[size];
            setVariantStocks(newStocks);
        } else {
            if (size === 'None') {
                setSelectedSizes(['None']);
                setVariantStocks({ None: { store: '0', wh: '0' } });
            } else if (selectedSizes.includes('None')) {
                addToast('warning', 'None is selected', 'Deselect "None" first to add specific sizes.');
            } else {
                setSelectedSizes(prev => [...prev, size]);
                setVariantStocks(prev => ({ ...prev, [size]: { store: 0, wh: 0 } }));
            }
        }
    };

    const updateVariantStock = (size: string, field: 'store' | 'wh', value: string) => {
        if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 10000)) {
            setVariantStocks(prev => ({
                ...prev,
                [size]: {
                    ...prev[size],
                    [field]: value
                }
            }));
        }
    };

    const handleNext = () => {
        if (step === 0) {
            const fe = {
                name: !formData.name.trim(),
                collection: !formData.collection,
                price: !formData.price || parseFloat(String(formData.price)) <= 0,
            };
            setFieldErrors(fe);
            if (fe.name || fe.collection || fe.price) {
                setError("Please fill in all required fields marked below.");
                return;
            }
        }
        setError(null);
        setFieldErrors({});
        setStep(prev => prev + 1);
    };

    const handleBack = () => {
        setError(null);
        setStep(prev => prev - 1);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (readOnly) return;

        if (selectedSizes.length === 0) {
            setError("Select at least one size, or choose None.");
            return;
        }

        if (!formData.collection) {
            setError("Please select a collection.");
            return;
        }

        const selectedCollectionObj = collections.find(c => c.name === formData.collection);
        if (!selectedCollectionObj) {
            setError("Invalid collection selected.");
            return;
        }

        const stocksArray = selectedSizes.map(sizeVal => {
            const stockData = variantStocks[sizeVal] || { store: 0, wh: 0 };
            const generatedSku = sizeVal === 'None'
                ? formData.id.trim()
                : `${formData.id.trim()}-${sizeVal}`;

            return {
                sku: generatedSku,
                size: sizeVal,
                stock: parseInt(stockData.store) || 0,
                warehouseStock: parseInt(stockData.wh) || 0
            };
        });

        const createUniqueArticleId = () => {
            const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let result = '';
            for (let i = 0; i < 9; i++) {
                result += characters.charAt(Math.floor(Math.random() * characters.length));
            }
            return result;
        };

        try {
            const isEdit = isEditMode && formData.id;

            // Delete any sizes that existed before but are no longer selected
            if (isEdit && originalStocks.length > 0) {
                const removedStocks = originalStocks.filter(s => !selectedSizes.includes(s.size));
                await Promise.all(
                    removedStocks
                        .filter(s => s.productStockId)
                        .map(s => fetch(`${process.env.PRODUCT_BASE_URL}/${formData.id.trim()}/stock/${s.productStockId}`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' },
                        }))
                );
            }

            const url = isEdit
                ? `${process.env.PRODUCT_BASE_URL}/${formData.id.trim()}`
                : process.env.PRODUCT_BASE_URL!;

            // POST: create product with stocks
            // PUT:  update metadata + upsert stocks (backend handles add/update per size)
            const stocksPayload = stocksArray.map(s => ({
                    size: s.size,
                    storeStock: s.stock,
                    warehouseStock: s.warehouseStock,
                }));

            const body = isEdit
                ? {
                    productName: formData.name.trim(),
                    collectionId: selectedCollectionObj.id,
                    image: formData.image.trim() || undefined,
                    price: parseFloat(formData.price) || 0,
                    stocks: stocksPayload,
                }
                : {
                    productName: formData.name.trim(),
                    collectionId: selectedCollectionObj.id,
                    image: formData.image.trim() ||
                        `https://placehold.co/400x400/e2e8f0/1e293b?text=${encodeURIComponent(formData.name)}`,
                    price: parseFloat(formData.price) || 0,
                    stocks: stocksPayload,
                };

            const addAProductApiRequest = await fetch(url, {
                method: isEdit ? "PUT" : "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            const addAProductApiResponse = await addAProductApiRequest.json();

            if (addAProductApiResponse.OK) {
                // Transform backend response to frontend Product shape
                const raw = addAProductApiResponse.DATA;
                const transformed = {
                    _id: raw.productId,
                    id: raw.productId,
                    name: raw.productName,
                    collection: selectedCollectionObj.name,
                    collectionId: raw.collectionId,
                    price: parseFloat(raw.price) || 0,
                    image: raw.image || '',
                    stocks: (raw.stocks || []).map((s: any) => ({
                        sku: `${raw.productId}-${s.size}`,
                        size: s.size,
                        stock: s.storeStock ?? 0,
                        warehouseStock: s.warehouseStock ?? 0,
                        productStockId: s.productStockId,
                        stockId: s.stockId,
                    })),
                };
                onSave(transformed as any);
                onClose();
            } else {
                setError(addAProductApiResponse.MSG || "Failed to create product.");
            }
        } catch (err) {
            console.error("API Error:", err);
            setError("Network error occurred. Please try again.");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === 'price' && value !== '' && (parseFloat(value) < 0 || parseFloat(value) > 1000000)) return;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError(null);
        if (fieldErrors[name as keyof typeof fieldErrors]) {
            setFieldErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh] border border-white/10 dark:border-gray-800 transition-all duration-300">
                <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-600 rounded-xl text-white">
                            <Package size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold dark:text-white uppercase tracking-tight">
                                {isEditMode ? 'Modify Article' : 'New Article Entry'}
                            </h3>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Article Identity System</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 scrollbar-hide">
                    {error && (
                        <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/50 rounded-2xl flex items-center gap-3 text-rose-600 dark:text-rose-400 text-sm animate-in slide-in-from-top-2">
                            <AlertCircle size={18} />
                            <p className="font-bold">{error}</p>
                        </div>
                    )}

                    {step === 0 && (
                        <div className="space-y-6 animate-in fade-in duration-300">
                            {/* Image Upload Section */}
                            <div className="flex flex-col items-center justify-center mb-8">
                                <div className="relative group">
                                    <div className={`w-40 h-40 rounded-3xl overflow-hidden bg-gray-50 dark:bg-gray-800 border-2 border-dashed transition-all flex items-center justify-center ${formData.image ? 'border-indigo-500 shadow-xl' : 'border-gray-200 dark:border-gray-700'}`}>
                                        {formData.image ? (
                                            <img src={formData.image} className="w-full h-full object-cover" alt="Preview" />
                                        ) : (
                                            <div className="flex flex-col items-center gap-2 text-gray-400">
                                                <ImageIcon size={40} strokeWidth={1.5} />
                                                <span className="text-[10px] font-black uppercase tracking-widest">No Image</span>
                                            </div>
                                        )}

                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                className="p-3 bg-white text-indigo-600 rounded-2xl hover:scale-110 transition-transform shadow-lg"
                                            >
                                                <Upload size={20} />
                                            </button>
                                            {formData.image && (
                                                <button
                                                    type="button"
                                                    onClick={clearImage}
                                                    className="p-3 bg-rose-500 text-white rounded-2xl hover:scale-110 transition-transform shadow-lg"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageChange}
                                />
                                <p className="mt-3 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">Click to upload article photo (Max 2MB)</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Article id *</label>
                                    <input disabled name="id" value={formData.id} onChange={handleChange} className="w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl font-mono text-sm border-none focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50" placeholder="Generated Automatically" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Design Name *</label>
                                    <input required name="name" value={formData.name} onChange={handleChange} className={`w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500 border-2 ${fieldErrors.name ? 'border-rose-400 focus:ring-rose-400' : 'border-transparent'}`} placeholder="Product Name" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                                        Collection Stream *
                                    </label>

                                    <select
                                        name="collection"
                                        required
                                        value={formData.collection}
                                        onChange={(e) => {
                                            if (e.target.value === "__add_new__") {
                                                setIsCollectionModalOpen(true);
                                                return;
                                            }
                                            handleChange(e);
                                        }}
                                        className={`w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl font-semibold text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all duration-200 border-2 ${fieldErrors.collection ? 'border-rose-400 focus:ring-rose-400' : 'border-gray-200 dark:border-gray-700'}`}
                                    >
                                        <option value="">Select Collection</option>

                                        {collections.map((c) => (
                                            <option key={c._id} value={c.name}>
                                                {c.name}
                                            </option>
                                        ))}

                                        <option disabled>──────────────</option>
                                        <option value="__add_new__">+ Add New Collection</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Retail Price (₨) *</label>
                                    <input required type="number" name="price" value={formData.price} onChange={handleChange} className={`w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl font-black text-sm outline-none focus:ring-2 focus:ring-indigo-500 border-2 ${fieldErrors.price ? 'border-rose-400 focus:ring-rose-400' : 'border-transparent'}`} placeholder="0" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* {step === 1 && (
                        <div className="space-y-6 animate-in fade-in duration-300">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Material Composition</label>
                                    <input name="material" value={formData.material} onChange={handleChange} className="w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-sm border-none outline-none" placeholder="e.g. Lawn / Silk" />
                                </div>
                                <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Color Palette</label>
                            <input name="color" value={formData.color} onChange={handleChange} className="w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-sm border-none outline-none" placeholder="Primary Color" />
                        </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Product Description</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} rows={4} className="w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-sm border-none outline-none resize-none" placeholder="Styling notes..." />
                            </div>
                            <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Or External Image URL</label>
                        <input name="image" value={formData.image.startsWith('data:') ? '' : formData.image} onChange={handleChange} className="w-full px-5 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-sm border-none outline-none" placeholder="https://..." />
                        <p className="text-[8px] text-gray-400 mt-1 uppercase tracking-tighter">Manual URL entry. Note: Uploading a file overrides this.</p>
                    </div>
                        </div>
                    )} */}

                    {step === 1 && (
                        <div className="space-y-8 animate-in fade-in duration-300">
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                {SIZE_OPTIONS.map(opt => (
                                    <button
                                        key={opt.size}
                                        type="button"
                                        onClick={() => handleToggleSize(opt.size)}
                                        className={`py-3 rounded-xl text-xs font-bold transition-all border ${
                                            selectedSizes.includes(opt.size)
                                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg'
                                                : opt.size === 'None'
                                                    ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 italic'
                                                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-400'
                                        }`}
                                    >
                                        {opt.size}
                                    </button>
                                ))}
                            </div>
                            {selectedSizes.length > 0 && (
                                <div className="space-y-3">
                                    {/* Header Row for Inputs */}
                                    <div className="grid grid-cols-12 gap-3 px-4 mb-1">
                                        <div className="col-span-4" /> {/* Spacer for Variant Name */}
                                        {userRole === 'Admin' ? (
                                            <>
                                                <div className="col-span-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Store Qty</div>
                                                <div className="col-span-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">WH Qty</div>
                                            </>
                                        ) : (
                                            <div className="col-start-9 col-span-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">WH Qty</div>
                                        )}
                                    </div>

                                    {selectedSizes.map((size) => {
                                        const opt = SIZE_OPTIONS.find((o) => o.size === size);
                                        const stocks = variantStocks[size] || { store: 0, wh: 0 };
                                        return (
                                            <div key={size} className="grid grid-cols-12 gap-3 items-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                                                {/* Variant Name */}
                                                <div className="col-span-4 text-xs font-bold dark:text-white uppercase tracking-widest">
                                                    {opt?.size}
                                                </div>

                                                {userRole === 'Admin' ? (
                                                    <>
                                                        {/* Store Qty Input */}
                                                        <div className="col-span-4">
                                                            <input
                                                                disabled={isStoreLocked}
                                                                type="number"
                                                                value={stocks.store}
                                                                onChange={(e) => updateVariantStock(size, 'store', e.target.value)}
                                                                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-none rounded-lg text-xs font-bold outline-none focus:ring-1 focus:ring-[#139F6B]"
                                                                placeholder="0"
                                                            />
                                                        </div>
                                                        {/* WH Qty Input */}
                                                        <div className="col-span-4">
                                                            <input
                                                                type="number"
                                                                value={stocks.wh}
                                                                onChange={(e) => updateVariantStock(size, 'wh', e.target.value)}
                                                                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-none rounded-lg text-xs font-bold outline-none focus:ring-1 focus:ring-[#139F6B]"
                                                                placeholder="0"
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    /* Non-Admin: Only WH Qty (Aligned to the last column) */
                                                    <div className="col-start-9 col-span-4">
                                                        <input
                                                            type="number"
                                                            value={stocks.wh}
                                                            onChange={(e) => updateVariantStock(size, 'wh', e.target.value)}
                                                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border-none rounded-lg text-xs font-bold outline-none focus:ring-1 focus:ring-[#139F6B]"
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 dark:border-gray-800 shrink-0 flex gap-4">
                    <button type="button" onClick={step === 0 ? onClose : handleBack} className="flex-1 py-3 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-200 rounded-xl font-bold text-sm uppercase tracking-widest transition-all">
                        {step === 0 ? 'Cancel' : 'Previous'}
                    </button>
                    <button type="button" onClick={step === 1 ? handleSubmit : handleNext} className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold text-sm uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none">
                        {step === 1 ? 'Save Article' : 'Continue'}
                    </button>
                </div>
            </div>
            {isCollectionModalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-900 w-[420px] rounded-2xl p-6 shadow-2xl animate-fadeIn">

                        <h2 className="text-lg font-bold mb-5">
                            Add New Collection
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-gray-400">
                                    Collection Name
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={newCollection.collectionName}
                                    onChange={(e) =>
                                        setNewCollection({ collectionName: e.target.value })
                                    }
                                    className="w-full mt-1 px-4 py-2 rounded-lg
                       border border-gray-200 dark:border-gray-700
                       bg-gray-50 dark:bg-gray-800
                       focus:ring-2 focus:ring-indigo-500 outline-none"
                                    placeholder="e.g. Winter Edit II"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setIsCollectionModalOpen(false)}
                                className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleAddCollection}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductModal;
